<template>
  <div class="app-wrapper">
    <div v-if="isSidebarOpen" class="sidebar-overlay" @click="isSidebarOpen = false"></div>

    <aside
      class="sidebar"
      :class="{ 'sidebar-open': isSidebarOpen, 'sidebar-collapsed': isSidebarCollapsed }"
      :aria-hidden="isSidebarCollapsed && !isSidebarOpen"
      :inert="isSidebarCollapsed && !isSidebarOpen"
    >
      <div class="logo">
        <img src="../assets/logo.png" alt="Company Logo" class="logo-img" />
        <span class="logo-text">合信進件管理系統</span>
      </div>

      <nav class="menu-list" :class="{ 'report-groups-first': reportGroupsFirst }">
        <template v-for="route in regularMenuItems" :key="route.path">
          <router-link
            :to="route.path"
            class="menu-item"
            active-class="active-link"
            @click="isSidebarOpen = false"
          >
            <span class="icon">▸</span>
            <span class="text">{{ route.meta?.title }}</span>

            <!-- 🌟 整合待辦數字：當該選單有大於 0 的數字時，顯示紅點標籤 -->
            <span v-if="getBadgeCount(route.name) > 0" class="badge">
              {{ getBadgeCount(route.name) }}
            </span>
          </router-link>
        </template>
        <details
          v-for="group in reportMenuGroups"
          :key="group.title"
          class="menu-group"
          :open="group.title === '統計報表'"
        >
          <summary class="menu-group-title">
            <span class="icon">▸</span>
            <span class="text">{{ group.title }}</span>
          </summary>
          <div class="submenu-list">
            <router-link
              v-for="route in group.items"
              :key="route.path"
              :to="route.path"
              class="menu-item submenu-item"
              active-class="active-link"
              @click="isSidebarOpen = false"
            >
              <span class="submenu-dot"></span>
              <span class="text">{{ route.meta?.title }}</span>
            </router-link>
          </div>
        </details>
      </nav>
    </aside>

    <button
      type="button"
      class="sidebar-collapse-btn"
      :class="{ 'is-collapsed': isSidebarCollapsed }"
      :aria-label="isSidebarCollapsed ? '顯示左側選單' : '隱藏左側選單'"
      :title="isSidebarCollapsed ? '顯示左側選單' : '隱藏左側選單'"
      @click="toggleSidebar"
    >
      {{ isSidebarCollapsed ? '▶' : '◀' }}
    </button>

    <div class="main-content">
      <header class="topbar">
        <div class="navigation-controls">
          <button
            class="hamburger-btn"
            @click="isSidebarOpen = !isSidebarOpen"
            aria-label="切換選單"
          >
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
        </div>

        <div class="user-info">
          <UserAccountMenu :greeting="displayGreeting" />
          <button @click="handleLogout" class="logout-btn">登出</button>
        </div>
      </header>

      <main class="page-content">
        <div v-if="showTestBanner" class="test-env-badge">
          目前位於{{ envName }}測試環境中，所有動作皆為測試行為。
        </div>
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRouter, type RouteRecordRaw, type RouteRecordName } from 'vue-router'
import { useDictionaryStore } from '@/stores/dictionaryStore'
import { useUserStore } from '@/stores/userStore'
// 🌟 1. 引入我們寫好的 Store 與 SignalR Composable
import { useDashboardStore } from '@/stores/dashboardStore'
import { useDashboardSignalR } from '@/composables/useDashboardSignalR'
import { useCarSetStore } from '@/stores/carSetStore'
import { useSmsStore } from '@/stores/smsStore'
import { authApi } from '@/api/auth'
import UserAccountMenu from '@/components/UserAccountMenu.vue'
import { PermissionCode, RoleCode, disabledReportPermissions } from '@/constants/accessControl'

const showTestBanner = ref(import.meta.env.VITE_SHOW_TEST_BANNER === 'true')
const envName = ref(import.meta.env.VITE_ENV_NAME || 'SITUST')

const dictStore = useDictionaryStore()
const userStore = useUserStore()
const dashboardStore = useDashboardStore() // 初始化 Dashboard Store
const carSetStore = useCarSetStore() // 初始化 CarSet Store
const smsStore = useSmsStore()

// 智慧選單開關狀態 (預設關閉)
const isSidebarOpen = ref(false)
const sidebarCollapsedStorageKey = 'layout-sidebar-collapsed'
const isSidebarCollapsed = ref(localStorage.getItem(sidebarCollapsedStorageKey) === 'true')
const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  localStorage.setItem(sidebarCollapsedStorageKey, String(isSidebarCollapsed.value))
}

// 🌟 2. 啟動 SignalR 監聽：收到後端推播時，執行 fetchStats() 重新撈取數字
useDashboardSignalR(() => {
  dashboardStore.fetchStats()
})

onMounted(() => {
  dictStore.fetchAll()
  // 🌟 3. 使用者剛登入載入框架時，主動撈取第一次最新數字
  dashboardStore.fetchStats()
  carSetStore.fetchCarSets() // 在頁面載入時，先撈取車型資料
  smsStore.fetchSms()
})

const router = useRouter()

const branchName = computed(() => userStore.branchName)
const distributorName = computed(() => userStore.distributorName)
const salesName = computed(() => userStore.salesName)
const role = computed(() => userStore.role)
const reportGroupsFirst = computed(
  () =>
    userStore.roleCode === RoleCode.HosinOwner ||
    userStore.roleCode === RoleCode.HosinManager ||
    userStore.roleCode === RoleCode.PartnerDistributor,
)

const displayGreeting = computed(() => {
  const displayName = userStore.userInfo?.displayName || userStore.userInfo?.userID || '使用者'
  const roleName = {
    [RoleCode.HosinOwner]: '高階主管',
    [RoleCode.HosinManager]: '業務主管',
    [RoleCode.HosinSales]: '合信業務員',
    [RoleCode.AccountAdmin]: '帳號管理員',
  }[userStore.roleCode ?? '']
  if (roleName) return `${displayName}（${roleName}）`
  switch (role.value) {
    case 'Dealer':
      return `${distributorName.value}（營業所）`
    case 'Branch':
      return `${distributorName.value}/${branchName.value}（據點）`
    case 'Sales':
      return `${distributorName.value}/${branchName.value}/${salesName.value}(業務員)`
    default:
      return '歡迎光臨'
  }
})

const menuItems = computed(() => {
  const rootRoute = router.options.routes.find((r: RouteRecordRaw) => r.path === '/')
  if (!rootRoute || !rootRoute.children) return []

  return rootRoute.children
    .filter((child) => {
      // 1. 隱藏設定為 hiddenInMenu 的項目
      if (child.meta?.hiddenInMenu || disabledReportPermissions.includes(String(child.meta?.permission))) return false

      const requiredPermissions =
        (child.meta?.permissions as string[] | undefined) ??
        (child.meta?.permission
          ? [child.meta.permission as string]
          : child.meta?.systemRoles
            ? [PermissionCode.CaseList]
            : [])
      if (
        requiredPermissions.length > 0 &&
        !requiredPermissions.some((permission) => userStore.hasPermission(permission))
      )
        return false
      const allowedRoleCodes = child.meta?.roleCodes as string[] | undefined
      if (
        allowedRoleCodes &&
        (!userStore.roleCode || !allowedRoleCodes.includes(userStore.roleCode))
      )
        return false

      return true
    })
    .map((child) => {
      // 處理路徑斜線問題
      const fullPath = child.path.startsWith('/') ? child.path : `/${child.path}`
      return {
        ...child,
        path: fullPath,
      }
    })
})
// 案件作業依處理順序排列，其餘案件進度由儀表板進入。
const caseMenuOrder = [
  'WebToContactList',
  'ReturnedList',
  'SupplementReturnList',
  'PendingSupplementList',
  'ConditionalApprovalList',
  'ApprovedPendingContractList',
  'RejectedList',
  'SelfCancelList',
  'TransferList',
  'AlreadyToAppropriationToDoList',
  'AppropriationReturnList',
  'ElectronicContractReturnList',
]
const regularMenuItems = computed(() => {
  const order = ['Dashboard', 'InqueryList', ...caseMenuOrder]
  const menuOrder = (name: RouteRecordName | null | undefined) => {
    const index = order.indexOf(String(name))
    return index < 0 ? order.length : index
  }
  return menuItems.value
    .filter((route) => !route.meta?.menuGroup)
    .filter((route) => {
      const isCaseList = /^\/(case|incoming|webincoming)\//.test(route.path)
      return !isCaseList || order.includes(String(route.name))
    })
    .sort((left, right) => menuOrder(left.name) - menuOrder(right.name))
})
const reportMenuOrder: Record<string, number> = {
  DailySalesConversionReport: 5,
  DailyWebsiteCaseSummaryReport: 10,
  PerformanceReport: 20,
  TemporaryMarketingReport: 30,
  AppropriationPerformanceReport: 40,
  SalesCaseTrackingReport: 50,
  SalesLeadDailyReport: 51,
  SalesAppropriationReport: 52,
  SalesPerformanceReport: 53,
  DailyWebsiteTrafficReport: 60,
  DownloadCenter: 70,
  RosterManagement: 100,
  UserManagement: 110,
  PermissionSettings: 120,
  ContactResultCodeManagement: 130,
  UatImportView: 140,
}
const reportMenuGroups = computed(() =>
  ['統計報表', '管理報表', '業務報表', '下載專區', '系統維護']
    .map((title) => ({
      title,
      items: menuItems.value
        .filter((route) => route.meta?.menuGroup === title)
        .sort(
          (left, right) =>
            (reportMenuOrder[String(left.name)] ?? Number.MAX_SAFE_INTEGER) -
            (reportMenuOrder[String(right.name)] ?? Number.MAX_SAFE_INTEGER),
        ),
    }))
    .filter((group) => group.items.length > 0),
)

// 🌟 4. 判斷各個選單該顯示哪個數字的智慧函數 (消滅 any，改用 RouteRecordName)
const getBadgeCount = (routeName: RouteRecordName | null | undefined): number => {
  if (!routeName) return 0

  const stats = dashboardStore.stats

  // 根據 router 設定的 name 來配對對應的 API 數字
  switch (routeName) {
    case 'TemporaryList': // 營業所暫存
      return stats.temporaryListCount || 0
    case 'ReadyToCaseToDoList': // 業務按出送件
      return stats.readyToCaseToDoListCount || 0
    case 'AlreadyToCaseToDoList': // 已送已進件清單
      return stats.alreadyToCaseToDoListCount || 0
    case 'AlreadyToReviewToDoList': // 審查中清單
      return stats.alreadyToReviewToDoListCount || 0
    case 'ReturnedList': // 進件退回清單
      return stats.returnedListCount || 0
    case 'CanceledList': // 作廢件清單
      return stats.canceledListCount || 0
    case 'WebTemporaryList': // 官網暫存件
      return stats.webTemporaryListCount || 0
    case 'WebToContactList': // 官網申請完成
      return stats.webToContactListCount || 0
    case 'WebConfirmedList': // 申請資料退回確認
      return stats.webConfirmedListCount || 0
    case 'ApprovedPendingContractList': // 核准(未簽約)清單
      return stats.approvedPendingContractListCount || 0
    case 'RejectedList': // 婉拒清單
      return stats.rejectedListCount || 0
    case 'PendingSupplementList': // 待補清單
      return stats.pendingSupplementListCount || 0
    case 'ConditionalApprovalList': // 附條件清單
      return stats.conditionalApprovalListCount || 0
    case 'AlreadyToAppropriationToDoList': // 簽約完成待撥款清單
      return stats.alreadyToAppropriationToDoListCount || 0
    case 'AlreadyToAppropriationList': // 已送撥款清單
      return stats.alreadyToAppropriationListCount || 0
    case 'AlreadyAccountingList': // 撥款完成清單
      return stats.alreadyAccountingListCount || 0
    case 'SelfCancelList': // 自行撤銷/取消清單
      return stats.selfCancelListCount || 0
    case 'TransferList':
      return stats.transferListCount || 0
    case 'SupplementList': // 補件清單
      return stats.supplementListCount || 0
    case 'SupplementReturnList': // 補件退回清單
      return stats.supplementReturnListCount || 0
    case 'AppropriationReturnList': // 撥款退回清單
      return stats.appropriationReturnListCount || 0
    case 'ElectronicContractReturnList':
      return stats.electronicContractReturnListCount || 0

    default:
      return 0
  }
}

const handleLogout = async () => {
  try {
    await authApi.logout()
  } finally {
    userStore.logout()
    await router.replace('/login')
  }
}
</script>

<style scoped>
/* =========================================
   品牌專屬配色變數 (Brand Colors)
========================================= */
.app-wrapper {
  --brand-gold: #c8a110;
  --brand-blue: #1a98c7;
  --sidebar-bg: #112234;
  --sidebar-hover: #1b263b;
  --bg-light: #f4f6f9;

  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: var(--bg-light);
}

/* =========================================
   左側側邊欄 (Sidebar)
========================================= */
.sidebar {
  width: 220px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100vh;
  background-color: var(--sidebar-bg);
  color: #f8fafc;
  display: flex;
  flex-direction: column;
  box-shadow: 3px 0 10px rgba(0, 0, 0, 0.15);
  z-index: 1040;
  overflow: hidden;
  transition:
    width 0.24s ease,
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar.sidebar-collapsed {
  width: 0;
  box-shadow: none;
}

.logo {
  height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 2px solid rgba(26, 152, 199, 0.3);
}

.logo-img {
  height: 30px;
  width: auto;
  object-fit: contain;
}

.logo-text {
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: var(--brand-gold);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.menu-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  &.report-groups-first .menu-group {
    order: -1;
  }

  .menu-group {
    color: #94a3b8;
  }

  .menu-group-title {
    display: flex;
    align-items: center;
    padding: 12px 18px;
    font-weight: 700;
    cursor: pointer;
    list-style: none;
    transition: all 0.3s ease;
  }

  .menu-group-title::-webkit-details-marker {
    display: none;
  }

  .menu-group-title:hover {
    background-color: var(--sidebar-hover);
    color: white;
  }

  .menu-group-title .icon {
    margin-right: 12px;
    transition: transform 0.2s ease;
  }

  .menu-group[open] > .menu-group-title .icon {
    transform: rotate(90deg);
  }

  .submenu-list {
    display: grid;
    gap: 3px;
    padding: 0 0 6px;
  }

  .submenu-item {
    padding: 10px 18px 10px 38px;
    font-size: 0.88rem;
  }

  .submenu-item:hover {
    padding-left: 42px;
  }

  .submenu-dot {
    width: 6px;
    height: 6px;
    margin-right: 11px;
    border-radius: 50%;
    background: currentColor;
  }

  padding: 15px 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  color: #94a3b8;
  text-decoration: none;
  transition: all 0.3s ease;
  border-right: 4px solid transparent;
}

.menu-item .icon {
  margin-right: 12px;
  font-size: 1.1rem;
}

.menu-item:hover {
  background-color: var(--sidebar-hover);
  color: white;
  padding-left: 22px;
}

.active-link {
  background-color: rgba(26, 152, 199, 0.15);
  color: white;
  border-right: 4px solid var(--brand-gold);
}

.active-link .icon {
  color: var(--brand-blue);
}

/* 🌟 新增：待辦數字的 UI 樣式 */
.badge {
  margin-left: auto; /* 將數字推到最右側 */
  background-color: #ef4444; /* 紅色醒目背景 */
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  padding: 3px 8px;
  border-radius: 9999px; /* 圓角膠囊形狀 */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* =========================================
   右側主內容區 (Main Content)
========================================= */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.topbar {
  height: 65px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 30px;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.02);
  z-index: 5;
}

.navigation-controls {
  display: flex;
  align-items: center;
}

.sidebar-collapse-btn {
  position: fixed;
  top: 15px;
  left: 220px;
  z-index: 1050;
  display: grid;
  width: 34px;
  height: 34px;
  transform: translateX(-50%);
  place-items: center;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  font-size: 0.78rem;
  transition:
    left 0.24s ease,
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.sidebar-collapse-btn.is-collapsed {
  left: 25px;
}

.sidebar-collapse-btn:hover {
  border-color: #1a98c7;
  background: #eef8fc;
  color: #0f6f96;
}

.hamburger-btn {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
}

.hamburger-line {
  width: 100%;
  height: 2.5px;
  background-color: #334155;
  border-radius: 2px;
  transition: all 0.2s;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
}

.greeting {
  color: #475569;
  font-weight: 600;
}

.logout-btn {
  flex-shrink: 0;
  background-color: transparent;
  color: #64748b;
  border: 1px solid #cbd5e1;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
}

.logout-btn:hover {
  background-color: #fef2f2;
  color: #ef4444;
  border-color: #ef4444;
}

.page-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1030;
}

@media (max-width: 768px) {
  .sidebar {
    width: 220px;
    position: fixed;
    left: 0;
    top: 0;
    transform: translateX(-100%);
  }

  .sidebar.sidebar-open {
    transform: translateX(0);
  }

  .sidebar.sidebar-collapsed {
    width: 220px;
  }

  .topbar {
    justify-content: space-between;
    padding: 0 15px;
    gap: 12px;
  }

  .user-info {
    gap: 8px;
  }

  .hamburger-btn {
    display: flex;
  }

  .sidebar-collapse-btn {
    display: none;
  }
}

.test-env-badge {
  /* 固定在右下角 */
  position: fixed;
  bottom: 15px;
  right: 15px;

  /* 視覺樣式設定 (保持顯眼但小巧) */
  background-color: #ff33cc;
  color: #ffffff;
  padding: 4px 10px; /* 縮小內距 */
  font-size: 12px; /* 縮小字體 */
  border-radius: 4px; /* 加上一點圓角 */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2); /* 加上陰影增加立體感 */

  /* 確保在最上層，且不會阻擋滑鼠點擊 */
  z-index: 9999;
  pointer-events: none;
}
</style>
